import { FunctionComponent, MouseEvent, useState } from "react";

import { faCheck, faCopy } from "@fortawesome/pro-regular-svg-icons";
import { faBuilding, faUser } from "@fortawesome/pro-solid-svg-icons";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";

import { Icon } from "@/components";

import { prioBadgeClasses, toScoreLabel } from "@/components/LeadScoringPopover/scoreTier";
import { copyText } from "@/utils/copyText";

export type CategoryReasoningKind = "company" | "persona";

interface CategoryReasoningPopoverProps {
	kind: CategoryReasoningKind;
	/** Resolved category (segment) name, e.g. "International Promotion Agency". */
	categoryName?: string | null;
	/** AI reasoning behind the category verdict. */
	reasoning?: string | null;
	/** Raw score driving the prio badge: companyScore for company, personaScore for persona. */
	prioScore?: string | null;
	onCopy?: (value: string) => void;
}

const HEAD: Record<CategoryReasoningKind, { icon: IconDefinition; label: string; prio: string }> = {
	company: { icon: faBuilding, label: "Company category", prio: "Company Prio" },
	persona: { icon: faUser, label: "Persona category", prio: "Persona Prio" },
};

const stop = (e: MouseEvent) => {
	e.preventDefault();
	e.stopPropagation();
};

/**
 * Beautified Company / Persona segment hover (PREC-3499): leads with the verdict
 * — a prio badge and the resolved category name — then the AI reasoning below,
 * with click-to-copy. Reuses the same card shell, divider and tier colours as
 * the contact / info / lead-scoring hovers. Tailwind-only (extension-safe).
 */
export const CategoryReasoningPopover: FunctionComponent<CategoryReasoningPopoverProps> = ({
	kind,
	categoryName,
	reasoning,
	prioScore,
	onCopy,
}) => {
	const [copied, setCopied] = useState(false);
	const head = HEAD[kind];

	if (!categoryName && !reasoning) {
		return null;
	}

	const copy = (e: MouseEvent) => {
		stop(e);

		if (!reasoning) {
			return;
		}

		void copyText(reasoning).then((copied) => {
			if (!copied) {
				return;
			}

			onCopy?.(reasoning);
			setCopied(true);
			setTimeout(() => setCopied(false), 1000);
		});
	};

	return (
		<div className="w-[300px]">
			<div className="flex items-center gap-1.5 px-2 pb-1 pt-0.5 text-[10px] font-bold uppercase tracking-wide text-text-meta">
				<Icon className="h-[12px] w-[12px]" icon={head.icon} />
				{head.label}
			</div>
			<div className="flex flex-col items-start gap-1 px-2 pb-2 pt-1">
				{prioScore && (
					<span
						className={`rounded-md px-2 py-0.5 text-[10px] font-extrabold uppercase ${prioBadgeClasses(
							toScoreLabel(prioScore)
						)}`}
					>
						{head.prio} {toScoreLabel(prioScore)}
					</span>
				)}
				{categoryName && (
					<span className="text-[13.5px] font-bold text-gray-900">{categoryName}</span>
				)}
			</div>
			{reasoning && (
				<>
					<div className="my-1 h-px bg-gray-200" />
					<div
						className={`group/val max-h-[300px] cursor-pointer overflow-y-auto rounded-md px-2 py-1.5 transition-colors ${
							copied ? "bg-status-success-light" : "hover:bg-gray-50"
						}`}
						onClick={copy}
					>
						<div className="flex items-start gap-2">
							<span className="flex-1 whitespace-normal text-[11.5px] leading-relaxed text-text-meta">
								{reasoning}
							</span>
							<Icon
								className={`mt-0.5 h-[14px] w-[14px] shrink-0 transition-opacity ${
									copied
										? "text-status-success opacity-100"
										: "text-text-meta opacity-0 group-hover/val:opacity-100"
								}`}
								icon={copied ? faCheck : faCopy}
							/>
						</div>
					</div>
				</>
			)}
		</div>
	);
};
